import { UseQueryResult, useQuery } from '@tanstack/react-query';

import {
  getUnreadNotifications,
  markNotificationsAsRead,
} from '../api/notifications';
import { alertResource } from '../api/notifications/resources';
import {
  GetNotificationsProps,
  NotificationData,
  NotificationKind,
} from '../api/notifications/types';
import { canSee } from '../helpers/roleAccess';
import { unwrap } from '../lib/envelope';
import { queryClient } from '../lib/queryClient';
import { useCurrentRole } from './useCurrentRole';

const KINDS = [NotificationKind.RECRUITMENT_CELL_CRITICAL];
const PAGE_SIZE = 50;

export const recruitmentAlertsKey = [
  'notifications',
  NotificationKind.RECRUITMENT_CELL_CRITICAL,
] as const;

const fetchAlerts = ({
  signal,
}: {
  signal?: AbortSignal;
}): Promise<GetNotificationsProps> =>
  getUnreadNotifications(KINDS, 1, PAGE_SIZE, signal).then(unwrap);

export { alertResource };

/**
 * Unread "par crítico" alerts of the Preenchimento page (REAL-3273). They are
 * written once a day by the snapshot job, so a slow poll is enough. Only for
 * the roles that open the page.
 */
export function useRecruitmentAlerts(): UseQueryResult<GetNotificationsProps> & {
  notifications: NotificationData[];
  unread: number;
} {
  const role = useCurrentRole();
  const query = useQuery({
    queryKey: recruitmentAlertsKey,
    queryFn: fetchAlerts,
    enabled: canSee('networkRecruitment', role),
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  const notifications = query.data?.data ?? [];
  return {
    ...query,
    notifications,
    unread: query.data?.detail.total_records ?? notifications.length,
  };
}

/**
 * Marks as read the alerts of one pair once its detail is open: the manager
 * is looking at it, the bell must stop pointing there. Silent on failure.
 */
export async function markRecruitmentAlertsSeen(
  poleId: number,
  criteriaGroupId: number
): Promise<void> {
  let current: GetNotificationsProps;
  try {
    current = await queryClient.fetchQuery({
      queryKey: recruitmentAlertsKey,
      queryFn: fetchAlerts,
      staleTime: 0,
    });
  } catch {
    return;
  }
  const ids = (current.data ?? [])
    .filter((item) => {
      const resource = alertResource(item);
      return (
        resource?.notice_coverage_id === poleId &&
        resource?.criteria_group_id === criteriaGroupId
      );
    })
    .map((item) => item.id);
  if (ids.length === 0) return;
  try {
    await markNotificationsAsRead(ids);
  } catch {
    return;
  }
  await queryClient.invalidateQueries({ queryKey: recruitmentAlertsKey });
}
